import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import { fetchEventsLookup } from "@/modules/emailManagement/features/EmailCampaignManagement/api/fetchEventsLookup/fetchEventsLookupApi";
import { fetchVenues } from "@/modules/eventRegistration/features/EventVenueManagement/api/fetchVenues/fetchVenuesApi";
import { loadAllFilterOptions } from "../../utils/loadAllFilterOptions";
import type { SessionsReportFilters } from "../../hooks/useSessionsReport";

type Option = { label: string; value: string };

interface SessionsReportFiltersProps {
  filters: SessionsReportFilters;
  isExporting: boolean;
  onFilterChange: (
    key: keyof SessionsReportFilters,
    value: string | number[] | undefined,
  ) => void;
  onExportCsv: () => void;
}

const SessionsReportFilters: React.FC<SessionsReportFiltersProps> = ({
  filters,
  isExporting,
  onFilterChange,
  onExportCsv,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [eventValue, setEventValue] = useState<string[]>(
    (filters.eventIds ?? []).map(String),
  );
  // Labels for selected events, kept separate from search results so a
  // selected chip keeps its name even when it falls outside the current query.
  const [eventSelected, setEventSelected] = useState<Option[]>([]);
  const [eventResults, setEventResults] = useState<Option[]>([]);
  const [eventSearch, setEventSearch] = useState("");
  const [locationIds, setLocationIds] = useState<string[]>(
    (filters.locationIds ?? []).map(String),
  );
  const [locationOptions, setLocationOptions] = useState<Option[]>([]);
  const [sessionDateFrom, setSessionDateFrom] = useState(
    filters.sessionDateFrom ?? "",
  );
  const [sessionDateTo, setSessionDateTo] = useState(
    filters.sessionDateTo ?? "",
  );

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const debouncedEventSearch = useDebouncedValue(eventSearch, 400);
  const isMounted = useRef(false);

  useEffect(() => {
    loadAllFilterOptions((page, pageSize) =>
      fetchVenues({ page, pageSize, sortBy: "name", sortOrder: "ASC" }).then(
        (response) => ({
          options: response.data.map((venue) => ({
            label: venue.name,
            value: String(venue.id),
          })),
          totalCount: response.meta.totalCount,
        }),
      ),
    ).then(setLocationOptions);
  }, []);

  useEffect(() => {
    let active = true;
    fetchEventsLookup({
      searchTerm: debouncedEventSearch || undefined,
      page: 1,
      pageSize: 50,
      sortBy: "eventName",
      sortOrder: "asc",
    }).then((response) => {
      if (!active) return;
      setEventResults(
        response.data.map((event) => ({
          label: event.name,
          value: String(event.id),
        })),
      );
    });
    return () => {
      active = false;
    };
  }, [debouncedEventSearch]);

  const eventOptions = useMemo(() => {
    const byValue = new Map<string, Option>();
    [...eventSelected, ...eventResults].forEach((option) => {
      byValue.set(option.value, option);
    });
    return Array.from(byValue.values());
  }, [eventSelected, eventResults]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleEventChange = (value: string[]) => {
    setEventValue(value);
    setEventSelected(
      eventOptions.filter((option) => value.includes(option.value)),
    );
    onFilterChange("eventIds", value.length ? value.map(Number) : undefined);
  };

  const handleLocationChange = (value: string[]) => {
    setLocationIds(value);
    onFilterChange("locationIds", value.length ? value.map(Number) : undefined);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateFrom(e.target.value);
    onFilterChange("sessionDateFrom", e.target.value || undefined);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateTo(e.target.value);
    onFilterChange("sessionDateTo", e.target.value || undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setEventValue([]);
    setEventSelected([]);
    setEventSearch("");
    setLocationIds([]);
    setSessionDateFrom("");
    setSessionDateTo("");
    onFilterChange("searchTerm", undefined);
    onFilterChange("eventIds", undefined);
    onFilterChange("locationIds", undefined);
    onFilterChange("sessionDateFrom", undefined);
    onFilterChange("sessionDateTo", undefined);
  }, [onFilterChange]);

  const hasActiveFilters =
    searchTerm ||
    eventValue.length ||
    locationIds.length ||
    sessionDateFrom ||
    sessionDateTo;

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Event, session, venue..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label>Event</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleEventChange}
            onSearch={setEventSearch}
            options={eventOptions}
            placeholder="All Events"
            searchPlaceholder="Search events..."
            value={eventValue}
          />
        </div>

        <div>
          <Label>Location</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleLocationChange}
            options={locationOptions}
            placeholder="All Locations"
            value={locationIds}
          />
        </div>

        <div>
          <Label htmlFor="sessionDateFrom">Session Date From</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateFrom"
            onChange={handleDateFromChange}
            type="date"
            value={sessionDateFrom}
          />
        </div>

        <div>
          <Label htmlFor="sessionDateTo">Session Date To</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateTo"
            onChange={handleDateToChange}
            type="date"
            value={sessionDateTo}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionsReportFilters;
